import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import { useWaterSlotRules, useWaterSlotRulesReady } from '@/hooks/useWaterSlotRules';
import { useCallback, useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInDays, format, parseISO, subDays } from 'date-fns';
import { Droplets, LayoutGrid, Table2 } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { phMinutesOfDay, todayInPH } from '@/lib/phDate';
import { getRoleLabel, useMembership } from '@/hooks/useMembership';
import { useIsPhone } from '@/hooks/useIsPhone';
import { useProfile } from '@/hooks/useProfile';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { ApiResponse, FarmOverviewResponse } from '@/types/api';
import { ALL_SLOTS, dayStatus, roundSlotAt } from '@/lib/waterModel';
import type { DayStatus } from '@/lib/waterModel';
import type { ReadingSlot } from '@/types/entry';
import {
  groupByCycle,
  groupByDate,
  groupBySlot,
  useAllPondsDayReadings,
  useCycleReadings,
  useDayReadings,
  usePondsForDate,
} from '@/components/entry/adaptive/entryData';
import { SinglePondGrid } from '@/components/entry/adaptive/SinglePondGrid';
import { AllPondsGrid } from '@/components/entry/adaptive/AllPondsGrid';
import { DateNav, CycleFillMap } from '@/components/entry/adaptive/EntryChrome';
import { PhoneToday } from '@/components/entry/phone/PhoneToday';
import { PhoneWater } from '@/components/entry/phone/PhoneWater';
import { headerActionClass } from '@/components/pond/page/format';

type Scope = 'single' | 'all';

interface EntrySearch {
  pond?: string;
  date?: string;
  scope?: Scope;
  slot?: ReadingSlot;
}

export const Route = createFileRoute('/_app/entry/')({
  validateSearch: (search: Record<string, unknown>): EntrySearch => ({
    pond: typeof search.pond === 'string' ? search.pond : undefined,
    date: typeof search.date === 'string' ? search.date : undefined,
    scope: search.scope === 'all' ? 'all' : search.scope === 'single' ? 'single' : undefined,
    slot: ALL_SLOTS.find((s) => s === search.slot),
  }),
  component: EntryPage,
});

function EntryPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { membership, isLoading: membershipLoading, isSupervisorOrAbove } = useMembership();
  const { profile } = useProfile();
  const farmId = membership?.farmId;
  const isPhone = useIsPhone();
  const [roundSlot] = useState(() => roundSlotAt(phMinutesOfDay()));

  const today = todayInPH();
  const date = search.date || today;
  const scope: Scope = search.scope || 'single';
  // Phone width: /entry without a pond is the lab tech's Today; with one it's
  // the keypad water screen. Desktop keeps the grid.
  const phoneView = isPhone && scope === 'single' ? (search.pond ? 'water' : 'today') : null;

  const { data: checklist, isLoading: pondsLoading } = usePondsForDate(farmId, date);
  const activePonds = useMemo(() => (checklist?.ponds ?? []).filter((p) => p.activeCycle), [checklist]);

  const selectedPondId = phoneView === 'today' ? undefined : search.pond || activePonds[0]?.id;
  const selectedPond = activePonds.find((p) => p.id === selectedPondId);
  const cycleId = selectedPond?.activeCycle?.id;
  // The checklist only lists ponds with an open crop, so farm emptiness comes from the overview.
  const { data: overview } = useQuery<ApiResponse<FarmOverviewResponse>>({
    queryKey: ['farm', 'overview', farmId],
    queryFn: () => api.get(`/api/v1/farms/${farmId}/overview`),
    staleTime: 60 * 1000,
    enabled: !!farmId && !phoneView && scope === 'single' && !selectedPond,
  });
  const farmHasNoPonds = overview?.data.ponds.length === 0;
  const doc = selectedPond?.activeCycle?.doc ?? null;
  // DOC 1 = stocking day (#191), so stocking was doc - 1 days ago
  const stockingDate = doc !== null && doc !== undefined ? format(subDays(parseISO(date), doc - 1), 'yyyy-MM-dd') : undefined;
  // All ponds: don't step back past the earliest stocking among the listed crops.
  const earliestStocking = activePonds
    .map((p) => format(subDays(parseISO(date), p.activeCycle!.doc - 1), 'yyyy-MM-dd'))
    .reduce<string | undefined>((min, d) => (min === undefined || d < min ? d : min), undefined);

  // Single-pond data
  const { data: dayRaw, isLoading: dayLoading, isError: dayError, refetch: refetchDay } = useDayReadings(farmId, scope === 'single' ? cycleId : undefined, date);
  const prevDate = format(subDays(parseISO(date), 1), 'yyyy-MM-dd');
  const gridCycleId = scope === 'single' && !phoneView ? cycleId : undefined;
  const { data: prevRaw } = useDayReadings(farmId, gridCycleId, prevDate);
  const { data: cycleRaw } = useCycleReadings(farmId, gridCycleId, stockingDate, today);

  // All-ponds data
  const { data: allRaw } = useAllPondsDayReadings(scope === 'all' ? farmId : undefined, date);

  const dayReadings = useMemo(() => groupBySlot(dayRaw ?? []), [dayRaw]);
  const prevDayReadings = useMemo(() => groupBySlot(prevRaw ?? []), [prevRaw]);
  const readingsByCycle = useMemo(() => groupByCycle(allRaw ?? []), [allRaw]);

  const rulesReady = useWaterSlotRulesReady(farmId);
  const slotOff = useWaterSlotRules(farmId);
  const { table } = useFarmThresholds(farmId);
  const dayStatuses = useMemo(() => {
    const m = new Map<string, DayStatus>();
    if (!stockingDate) return m;
    // Until the table loads only the 'critical' colouring is missing; complete/partial/empty still show.
    const stock = parseISO(stockingDate);
    for (const [dStr, bySlot] of groupByDate(cycleRaw ?? [])) {
      m.set(dStr, dayStatus(table, bySlot, differenceInDays(parseISO(dStr), stock) + 1, undefined, slotOff));
    }
    return m;
  }, [cycleRaw, stockingDate, table, slotOff]);

  const onSaved = () => {
    if (!farmId) return;
    queryClient.invalidateQueries({ queryKey: ['entry-cycle-readings', farmId, cycleId] });
    queryClient.invalidateQueries({ queryKey: ['readings', farmId, cycleId] });
    queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
    queryClient.invalidateQueries({ queryKey: ['entry-allponds-readings', farmId] });
  };

  // Stable so the phone screen's autosave doesn't flush early on every parent render.
  const onPhoneSaved = useCallback(() => {
    if (!farmId) return;
    queryClient.invalidateQueries({ queryKey: ['entry-cycle-readings', farmId, cycleId] });
    queryClient.invalidateQueries({ queryKey: ['readings', farmId, cycleId] });
    queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
    queryClient.invalidateQueries({ queryKey: ['entry-allponds-readings', farmId] });
  }, [queryClient, farmId, cycleId]);

  const setDate = (d: string) => navigate({ search: (prev) => ({ ...prev, date: d }) });
  const setScope = (s: Scope) => navigate({ search: (prev) => ({ ...prev, scope: s }) });
  const setPond = (id: string) => navigate({ search: (prev) => ({ ...prev, pond: id, scope: 'single' }) });

  if (membershipLoading || pondsLoading || !rulesReady) {
    return <div className="flex justify-center py-20"><LoadingSpinner /></div>;
  }

  if (phoneView === 'today') {
    const role = membership?.role;
    const name = profile?.name || profile?.email;
    return (
      <PhoneToday
        farmId={farmId}
        date={date}
        linkDate={search.date}
        onDate={(d) => navigate({ search: (prev) => ({ ...prev, date: d === today ? undefined : d }) })}
        assignedIds={membership?.role === 'technician' ? membership.assignedPondIds : null}
        userLabel={role ? `${name ? `${name} ` : ''}(${getRoleLabel(role)})` : (name ?? null)}
      />
    );
  }

  if (phoneView === 'water') {
    if (!selectedPond || !cycleId) {
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500">
          <p>This pond has no active crop on this date.</p>
          <Link to="/entry" search={{}} className="mt-3 inline-flex min-h-11 items-center font-semibold text-primary-600">
            Back to Today
          </Link>
        </div>
      );
    }
    if (dayLoading) {
      return <div className="flex justify-center py-20"><LoadingSpinner /></div>;
    }
    if (dayError) {
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600">
          <p>Couldn't load this pond's readings.</p>
          <button
            type="button"
            onClick={() => refetchDay()}
            className="mt-3 min-h-11 rounded-lg px-4 font-semibold text-primary-600"
          >
            Try again
          </button>
        </div>
      );
    }
    return (
      <PhoneWater
        key={`${cycleId}|${date}`}
        farmId={farmId!}
        cycleId={cycleId}
        pondId={selectedPond.id}
        pondName={selectedPond.name}
        doc={doc}
        date={date}
        dayReadings={dayReadings}
        initialSlot={search.slot ?? roundSlot}
        onSaved={onPhoneSaved}
      />
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Droplets className="h-6 w-6 text-primary-500" />
          <h1 className="text-xl font-semibold text-gray-900">Water Entry</h1>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
          <ScopeButton active={scope === 'single'} onClick={() => setScope('single')} icon={<Table2 className="h-4 w-4" />} label="Single pond" />
          <ScopeButton active={scope === 'all'} onClick={() => setScope('all')} icon={<LayoutGrid className="h-4 w-4" />} label="All ponds" />
        </div>
      </header>

      <DateNav
        date={date}
        today={today}
        doc={scope === 'single' ? doc : null}
        minDate={scope === 'single' ? stockingDate : earliestStocking}
        cycleLabel={scope === 'single' ? selectedPond?.name : undefined}
        dayStatuses={dayStatuses}
        onDate={setDate}
      />

      {scope === 'single' && (
        <>
          {activePonds.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {activePonds.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPond(p.id)}
                  className={cn(
                    'rounded-full px-3 py-1 text-sm',
                    p.id === selectedPondId ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  )}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
          {/* Desktop's checklist only lists stocked crops, so Feeding and Sampling are always open here. */}
          {selectedPond && cycleId && (
            <nav aria-label={`${selectedPond.name} actions`} className="flex flex-wrap gap-2">
              <Link to="/entry/pond/$pondId/feeding" params={{ pondId: selectedPond.id }} search={{ date: search.date }} className={headerActionClass()}>
                Feeding
              </Link>
              <Link to="/entry/pond/$pondId/application" params={{ pondId: selectedPond.id }} search={{ date: search.date }} className={headerActionClass()}>
                + Application
              </Link>
              <Link to="/entry/pond/$pondId/sampling" params={{ pondId: selectedPond.id }} search={{ date: search.date }} className={headerActionClass()}>
                Sampling
              </Link>
              <Link to="/ponds/$pondId" params={{ pondId: selectedPond.id }} search={{}} className={headerActionClass()}>
                Pond details
              </Link>
            </nav>
          )}
          <CycleFillMap minDate={stockingDate} today={today} date={date} dayStatuses={dayStatuses} onPick={setDate} />
          {selectedPond && cycleId ? (
            <SinglePondGrid
              key={`${cycleId}|${date}`}
              farmId={farmId!}
              cycleId={cycleId}
              date={date}
              dayReadings={dayReadings}
              prevDayReadings={prevDayReadings}
              onSaved={onSaved}
            />
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-400">
              {farmHasNoPonds ? (
                <>
                  <p>No ponds in this farm yet.</p>
                  {isSupervisorOrAbove && (
                    <Link to="/ponds" className="mt-3 inline-flex min-h-11 items-center font-semibold text-primary-600">
                      Add ponds
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <p>No pond with an active crop on this date.</p>
                  <p className="mt-2 text-sm">
                    Preparing ponds: log water checks and applications on the pond&apos;s Pre-stocking tab
                    {isSupervisorOrAbove ? '.' : ', or ask your Farm Technician to stock it.'}
                  </p>
                  <Link to="/ponds" className="mt-3 inline-flex min-h-11 items-center font-semibold text-primary-600">
                    Open ponds
                  </Link>
                </>
              )}
            </div>
          )}
        </>
      )}

      {scope === 'all' && (
        <AllPondsGrid ponds={activePonds} readingsByCycle={readingsByCycle} onPick={setPond} />
      )}
    </div>
  );
}

function ScopeButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium',
        active ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
      )}
    >
      {icon}
      {label}
    </button>
  );
}
